/**
 * Reverse Geocoding Utility — GPS City Detection
 * Uses Nominatim (OpenStreetMap) — free, no API key, supports FR + AR for Morocco.
 */

// ── In-memory cache: key = "lat2,lng2" (2 decimal precision ≈ 1.1km grid) ──
const _geocodeCache = {};

/**
 * Extract the best city name from a Nominatim address object.
 * Prefers: city > town > village > county > state
 */
const extractCity = (address) => {
  if (!address) return null;
  return (
    address.city ||
    address.town ||
    address.village ||
    address.municipality ||
    address.county ||
    address.state_district ||
    address.state ||
    null
  );
};

/**
 * Reverse geocode lat/lng → city name.
 * Results are cached per ~1km grid cell to minimise API calls.
 * @param {number} lat
 * @param {number} lng
 * @param {string} lang - 'fr' or 'ar'
 * @returns {Promise<string|null>}
 */
export const reverseGeocode = async (lat, lng, lang = 'fr') => {
  if (!lat || !lng) return null;

  // Round to 2 decimal places for cache key (~1.1 km resolution)
  const key = `${lat.toFixed(2)},${lng.toFixed(2)},${lang}`;
  if (_geocodeCache[key] !== undefined) return _geocodeCache[key];

  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=${lang}&zoom=10`;
    const res = await fetch(url, {
      headers: {
        'Accept': 'application/json',
        // Nominatim usage policy: identify your app
        'User-Agent': 'CarTrackApp/1.0'
      }
    });
    if (!res.ok) throw new Error(`Nominatim ${res.status}`);
    const data = await res.json();
    const city = extractCity(data.address);
    _geocodeCache[key] = city;
    return city;
  } catch (err) {
    console.warn('[Geocoding] Reverse geocode failed:', err.message);
    _geocodeCache[key] = null; // cache null to avoid retrying immediately
    return null;
  }
};

// ── Geofence History (localStorage) ──────────────────────────────────────────
const HISTORY_KEY = 'geoNotifHistory';
const MAX_HISTORY = 100;

/**
 * Get the geofence history array from localStorage.
 * @returns {Array}
 */
export const getGeofenceHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/**
 * Add a geofence event to history.
 * @param {{ id, plate, brand, model, fromCity, toCity, type, lat, lng, timestamp }} entry
 */
export const addGeofenceEvent = (entry) => {
  try {
    const history = getGeofenceHistory();
    history.unshift(entry); // newest first
    if (history.length > MAX_HISTORY) history.length = MAX_HISTORY;
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  } catch (err) {
    console.warn('[Geocoding] Failed to save geofence event:', err.message);
  }
};

/**
 * Clear all geofence history.
 */
export const clearGeofenceHistory = () => {
  localStorage.removeItem(HISTORY_KEY);
};

// ── Last-known city per vehicle (localStorage) ────────────────────────────────
// Single source of truth for de-duplicating "city entered" notifications.
// Persists across page refreshes, background tracking and app restarts, so a
// vehicle that stays in the same city never re-triggers a notification.
const LAST_CITY_KEY = 'geoLastCityMap';

const _readLastCityMap = () => {
  try {
    const raw = localStorage.getItem(LAST_CITY_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

/** Get the last known city stored for a vehicle (or null). */
export const getLastCity = (carId) => {
  if (!carId) return null;
  return _readLastCityMap()[carId] || null;
};

/**
 * Persist the last known city for a vehicle.
 * This is a synchronous read-modify-write, so it acts as an atomic guard
 * against duplicate notifications fired by overlapping GPS updates.
 */
export const setLastCity = (carId, city) => {
  if (!carId) return;
  try {
    const map = _readLastCityMap();
    map[carId] = city;
    localStorage.setItem(LAST_CITY_KEY, JSON.stringify(map));
  } catch (err) {
    console.warn('[Geocoding] Failed to persist last city:', err.message);
  }
};

/** Normalize a city name for safe comparison (trim + lowercase). */
export const normalizeCity = (c) => (c || '').trim().toLowerCase();

// ── City-change decision engine (persisted, anti-spam) ────────────────────────
// Decides whether a freshly detected city should trigger ONE notification.
// Guarantees: no alert while the vehicle stays in a city; transient GPS/boundary
// flips are ignored (a new city must be confirmed across several readings); a
// city that was just notified is not re-notified within a cooldown window.
// State persists in localStorage so it survives refresh / restart / background.
const CITY_STATE_KEY = 'geoCityState';
const DEFAULT_CONFIRM_COUNT = 2;            // readings required to confirm a new city
const DEFAULT_RENOTIFY_COOLDOWN_MS = 60 * 60 * 1000; // 1h: don't re-alert same city

const _readCityState = () => {
  try {
    const raw = localStorage.getItem(CITY_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};
const _writeCityState = (s) => {
  try { localStorage.setItem(CITY_STATE_KEY, JSON.stringify(s)); } catch (_) {}
};

/**
 * Evaluate a newly detected city for a vehicle.
 * @param {string} carId
 * @param {string} newCityRaw - city name from reverse geocoding
 * @param {{ fallbackLast?: string, confirmCount?: number, cooldownMs?: number }} [opts]
 *        fallbackLast: last city from DB, used to seed state after a fresh restart.
 * @returns {{ notify: boolean, prevCity: string|null, city: string|null }}
 */
export const evaluateCityChange = (carId, newCityRaw, opts = {}) => {
  const out = { notify: false, prevCity: null, city: null };
  if (!carId || !newCityRaw) return out;
  const newCity = String(newCityRaw).trim();
  if (!newCity) return out;
  out.city = newCity;

  const newNorm = normalizeCity(newCity);
  const now = Date.now();
  const confirmCount = opts.confirmCount ?? DEFAULT_CONFIRM_COUNT;
  const cooldownMs = opts.cooldownMs ?? DEFAULT_RENOTIFY_COOLDOWN_MS;

  const all = _readCityState();
  const st = all[carId] || {};
  st.recent = st.recent || {};

  // Seed the "last city" from DB if we have no local state yet (post-restart).
  const last = st.last || opts.fallbackLast || null;

  // 1) First time we ever see a city for this vehicle → set baseline, no alert.
  if (!last) {
    all[carId] = { last: newCity, lastAt: now, pending: null, pendingCount: 0, recent: { [newNorm]: now } };
    _writeCityState(all);
    return out;
  }

  // 2) Same city as last confirmed → vehicle is staying, no alert.
  if (normalizeCity(last) === newNorm) {
    all[carId] = { ...st, last, pending: null, pendingCount: 0, recent: { ...st.recent, [newNorm]: now } };
    _writeCityState(all);
    return out;
  }

  // 3) Different city → require confirmation across consecutive readings to
  //    ignore transient flips (GPS jitter near city boundaries).
  if (st.pending !== newNorm) {
    all[carId] = { ...st, last, pending: newNorm, pendingCount: 1 };
    _writeCityState(all);
    return out;
  }
  const pendingCount = (st.pendingCount || 1) + 1;
  if (pendingCount < confirmCount) {
    all[carId] = { ...st, last, pending: newNorm, pendingCount };
    _writeCityState(all);
    return out;
  }

  // 4) Change confirmed. Commit the new city as the current one.
  const prevCity = last;
  const recentTs = st.recent[newNorm];
  const committed = { last: newCity, lastAt: now, pending: null, pendingCount: 0, recent: { ...st.recent, [newNorm]: now } };
  all[carId] = committed;
  _writeCityState(all);

  // 5) Cooldown: suppress if we already alerted this city very recently
  //    (handles two cities straddling a boundary).
  if (recentTs && (now - recentTs) < cooldownMs) {
    return { notify: false, prevCity, city: newCity };
  }

  return { notify: true, prevCity, city: newCity };
};

/** Reset the city-change state (all vehicles or a single one). */
export const resetCityState = (carId) => {
  if (!carId) { localStorage.removeItem(CITY_STATE_KEY); return; }
  const all = _readCityState();
  delete all[carId];
  _writeCityState(all);
};
